import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  API,
  clearToken,
  client,
  getToken,
  registerUnauthorizedHandler,
  setToken,
  unwrap,
} from "@/lib/api-base";
import { clearAll as clearLookupCache } from "@/lib/lookup-cache";

/**
 * AuthContext — stato globale di autenticazione.
 *
 * Stati possibili:
 *  - status = "loading" (validazione token in corso)
 *  - status = "authenticated" (user popolato)
 *  - status = "unauthenticated" (mostrare login)
 *
 * L'IdleTimer è configurabile via Impostazioni → Preferenze
 * (`session_timeout_minutes`). Default 20 minuti se la lettura fallisce.
 * Reset su `click`/`keydown`/`touchstart` (movimenti mouse ignorati,
 * come da requisito operativo).
 */
const DEFAULT_IDLE_MINUTES = 20;
const IDLE_MS_STORAGE_KEY = "pgi.idle_timeout_minutes";

function readCachedTimeoutMinutes() {
  try {
    const raw = localStorage.getItem(IDLE_MS_STORAGE_KEY);
    const n = raw ? parseInt(raw, 10) : NaN;
    if (Number.isFinite(n) && n >= 5 && n <= 120) return n;
  } catch {
    /* SSR / storage disabled */
  }
  return DEFAULT_IDLE_MINUTES;
}

export function storeTimeoutMinutes(minutes) {
  try {
    localStorage.setItem(IDLE_MS_STORAGE_KEY, String(minutes));
  } catch {
    /* ignore */
  }
}

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
};

export function AuthProvider({ children }) {
  const [status, setStatus] = useState("loading");
  const [user, setUser] = useState(null);
  const [autoLogout, setAutoLogout] = useState(false);
  const [idleMinutes, setIdleMinutes] = useState(readCachedTimeoutMinutes);
  const idleTimerRef = useRef(null);

  // --- Logout centralizzato ---
  const doLogout = useCallback(
    ({ reason } = {}) => {
      clearToken();
      clearLookupCache();
      setUser(null);
      setStatus("unauthenticated");
      if (reason === "idle" || reason === "unauthorized") setAutoLogout(true);
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    },
    [],
  );

  // --- Reset del timer inattività ---
  const resetIdle = useCallback(() => {
    if (status !== "authenticated") return;
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      doLogout({ reason: "idle" });
    }, idleMinutes * 60 * 1000);
  }, [status, doLogout, idleMinutes]);

  // --- Attaccatura degli event listener per il timer ---
  useEffect(() => {
    if (status !== "authenticated") return undefined;
    resetIdle(); // avvia timer iniziale
    // Interazione ESPLICITA (click/keydown/touch), non mousemove.
    const events = ["click", "keydown", "touchstart"];
    for (const ev of events) window.addEventListener(ev, resetIdle, { passive: true });
    return () => {
      for (const ev of events) window.removeEventListener(ev, resetIdle);
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [status, resetIdle]);

  // --- Registrazione handler 401 globale (dal client axios) ---
  useEffect(() => {
    registerUnauthorizedHandler(() => doLogout({ reason: "unauthorized" }));
    return () => registerUnauthorizedHandler(() => {});
  }, [doLogout]);

  // --- Bootstrap: se c'è già un token, chiedi /me + aggiorna timeout ---
  useEffect(() => {
    const token = getToken();
    // Aggiorna il timeout dall'endpoint pubblico (best-effort)
    (async () => {
      try {
        const res = await axios.get(`${API}/customizations/app-settings/public`);
        const m = res.data?.session_timeout_minutes;
        if (Number.isFinite(m) && m >= 5 && m <= 120) {
          setIdleMinutes(m);
          storeTimeoutMinutes(m);
        }
      } catch {
        /* fallback al valore cached/default */
      }
    })();
    if (!token) {
      setStatus("unauthenticated");
      return;
    }
    (async () => {
      try {
        const me = await unwrap(client.get("/auth/me"));
        setUser({ username: me.username });
        setStatus("authenticated");
      } catch {
        // Il response interceptor ha già gestito il clear.
        setStatus("unauthenticated");
      }
    })();
  }, []);

  // --- Login ---
  const login = useCallback(async (username, password) => {
    // Uso axios diretto (non `client`) per bypassare gli interceptor:
    // vogliamo che eventuali 401 restino qui, non triggerino il global
    // unauthorized handler.
    const res = await axios.post(`${API}/auth/login`, { username, password });
    const { token, username: uname } = res.data;
    setToken(token);
    setUser({ username: uname });
    setStatus("authenticated");
    setAutoLogout(false);
  }, []);

  // --- Logout esplicito ---
  const logout = useCallback(async () => {
    try {
      await client.post("/auth/logout");
    } catch {
      /* ignore — logout è client-side */
    }
    doLogout({ reason: "manual" });
  }, [doLogout]);

  const value = useMemo(
    () => ({
      status,
      user,
      login,
      logout,
      autoLogout,
      clearAutoLogout: () => setAutoLogout(false),
      idleMinutes,
      setIdleMinutes: (m) => {
        setIdleMinutes(m);
        storeTimeoutMinutes(m);
      },
    }),
    [status, user, login, logout, autoLogout, idleMinutes],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
